import React, { useState } from 'react';
import { motion } from 'framer-motion';

const StudentFeedback = () => {
  const [rating, setRating] = useState(0);
  const [hover, setHover] = useState(0);
  const [feedback, setFeedback] = useState('');
  const [submitted, setSubmitted] = useState(false);
  const [name, setName] = useState('');
  const [course, setCourse] = useState('');

  const courses = [
    "Web Development Fundamentals",
    "Advanced JavaScript",
    "React Masterclass",
    "Node.js Backend",
    "Database Design",
    "UI/UX Principles"
  ];
  const ratings = [
    "Poor",
    "Fair",
    "Good",
    "Very Good",
    "Excellent"
  ];

  // SVG Emojis
  const emojis = [
    <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" className="w-7 h-7">
      <circle cx="12" cy="12" r="10.5" fill="#FFCC4D" stroke="#664500" strokeWidth="1.5" />
      <path strokeLinecap="round" strokeLinejoin="round" stroke="#664500" strokeWidth="1.5" d="M15.182 16.318A4.486 4.486 0 0 0 12.016 15a4.486 4.486 0 0 0-3.198 1.318" />
      <path strokeLinecap="round" strokeLinejoin="round" stroke="#664500" strokeWidth="1.5" d="M9.75 9.75c0 .414-.168.75-.375.75S9 10.164 9 9.75 9.168 9 9.375 9s.375.336.375.75Zm5.625 0c0 .414-.168.75-.375.75s-.375-.336-.375-.75.168-.75.375-.75.375.336.375.75Z" />
    </svg>,
    <svg key="slightly-happy" viewBox="0 0 36 36" className="w-8 h-8">
      <circle cx="18" cy="18" r="16" fill="#FFCC4D" />
      <path d="M12 19C12 19 14 16 18 16C22 16 24 19 24 19" stroke="#664500" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="14" r="2" fill="#664500" />
      <circle cx="24" cy="14" r="2" fill="#664500" />
    </svg>,
    <svg key="neutral" viewBox="0 0 36 36" className="w-8 h-8">
      <circle cx="18" cy="18" r="16" fill="#FFCC4D" />
      <path d="M12 21H24" stroke="#664500" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="14" r="2" fill="#664500" />
      <circle cx="24" cy="14" r="2" fill="#664500" />
    </svg>,
    <svg key="slightly-sad" viewBox="0 0 36 36" className="w-8 h-8">
      <circle cx="18" cy="18" r="16" fill="#FFCC4D" />
      <path d="M12 21C12 21 14 24 18 24C22 24 24 21 24 21" stroke="#664500" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="14" r="2" fill="#664500" />
      <circle cx="24" cy="14" r="2" fill="#664500" />
    </svg>,
    <svg key="sad" viewBox="0 0 36 36" className="w-8 h-8">
      <circle cx="18" cy="18" r="16" fill="#FFCC4D" />
      <path d="M12 21C12 21 14 23 18 23C22 23 24 21 24 21" stroke="#664500" strokeWidth="2" strokeLinecap="round" />
      <circle cx="12" cy="14" r="2" fill="#664500" />
      <circle cx="24" cy="14" r="2" fill="#664500" />
    </svg>
  ];

  const emojiColors = ["bg-red-100", "bg-orange-100", "bg-yellow-100", "bg-blue-100", "bg-green-100"];

  // SVG Star component
  const StarIcon = ({ filled }) => (
    <svg
      viewBox="0 0 24 24"
      className={`w-8 h-8 ${filled ? 'text-yellow-400' : 'text-gray-300'}`}
      fill="currentColor"
    >
      <path d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z" />
    </svg>
  );

  const handleSubmit = (e) => {
    e.preventDefault();
    setSubmitted(true);
  };

  if (submitted) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
        className="bg-gradient-to-br from-[#0d4f5c]/5 to-[#1a7a8a]/5 flex items-center justify-center p-4 min-h-screen"
      >
        <div className="bg-white rounded-2xl shadow-xl p-8 max-w-md w-full text-center">
          <motion.div
            animate={{
              scale: [1, 1.2, 1],
              rotate: [0, 10, -10, 0]
            }}
            transition={{ duration: 0.7 }}
          >
            <div className={`mx-auto w-24 h-24 rounded-full ${emojiColors[rating - 1]} flex items-center justify-center mb-6`}>
              <div className="w-12 h-12">
                {emojis[rating - 1]}
              </div>
            </div>
          </motion.div>
          <h2 className="text-3xl font-bold text-gray-800 mt-2 mb-3">Feedback Submitted!</h2>
          <p className="text-gray-600 mb-3">Thank you for helping us improve the learning experience.</p>
          <div className="bg-gradient-to-r from-[#0d4f5c]/10 to-[#1a7a8a]/10 rounded-lg p-4 mb-6 text-left">
            <p className="font-medium text-[#0d4f5c] flex items-center">
              Your rating:
              <span className="flex ml-2">
                {[...Array(5)].map((_, i) => (
                  <StarIcon key={i} filled={i < rating} />
                ))}
              </span>
            </p>
            {feedback && <p className="mt-2 text-[#0d4f5c]">"<i>{feedback}</i>"</p>}
          </div>
          <motion.button
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => {
              setSubmitted(false);
              setRating(0);
              setFeedback('');
            }}
            className="bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] hover:from-[#0d4f5c]/90 hover:to-[#1a7a8a]/90 text-white font-medium py-3 px-8 rounded-full transition-all duration-300 shadow-md"
          >
            Submit Another Feedback
          </motion.button>
        </div>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      className="bg-gradient-to-br from-[#0d4f5c]/5 to-[#1a7a8a]/5 flex items-center justify-center p-4 min-h-screen"
    >
      <div className="bg-white rounded-2xl shadow-xl overflow-hidden w-full">
        <div className="md:flex">
          <div className="md:block md:w-2/5 bg-gradient-to-b from-[#0d4f5c] to-[#1a7a8a] p-8 flex flex-col justify-center">
            <div className="text-center mb-6">
              <svg className="w-16 h-16 mx-auto text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m5.618-4.016A11.955 11.955 0 0112 2.944a11.955 11.955 0 01-8.618 3.04A12.02 12.02 0 003 9c0 5.591 3.824 10.29 9 11.622 5.176-1.332 9-6.03 9-11.622 0-1.042-.133-2.052-.382-3.016z" />
              </svg>
            </div>
            <h2 className="text-white text-2xl font-bold mb-4 text-center">Deplomo Student Feedback</h2>
            <p className="text-blue-100 text-center">Your honest feedback helps us create better courses and learning experiences.</p>
            <div className="mt-8 flex flex-col items-center">
              <svg className="w-48 h-48" viewBox="0 0 200 200" fill="none" xmlns="http://www.w3.org/2000/svg">
                {/* Glowing background effect */}
                <circle cx="100" cy="100" r="90" fill="rgba(255,255,255,0.03)" />
                <circle cx="100" cy="100" r="70" fill="rgba(255,255,255,0.05)" />

                {/* Animated rings */}
                <circle cx="100" cy="100" r="80" stroke="rgba(255,255,255,0.1)" strokeWidth="1.5" strokeDasharray="8 8" />
                <circle cx="100" cy="100" r="60" stroke="rgba(255,255,255,0.15)" strokeWidth="1" strokeDasharray="4 4" />

                {/* Main icon group - Modern graduation cap with diploma */}
                <g transform="translate(100, 95)">
                  {/* Diploma behind cap */}
                  <rect x="-30" y="-30" width="60" height="45" rx="3" fill="rgba(255,255,255,0.15)" transform="rotate(-15, 0, 0)" />
                  <rect x="-28" y="-28" width="56" height="41" rx="2" fill="rgba(255,255,255,0.1)" transform="rotate(-15, 0, 0)" />

                  {/* Decorative lines on diploma */}
                  <line x1="-20" y1="-18" x2="20" y2="-18" stroke="rgba(255,255,255,0.2)" strokeWidth="2" transform="rotate(-15, 0, 0)" />
                  <line x1="-20" y1="-10" x2="20" y2="-10" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" transform="rotate(-15, 0, 0)" />
                  <line x1="-20" y1="-2" x2="15" y2="-2" stroke="rgba(255,255,255,0.15)" strokeWidth="1.5" transform="rotate(-15, 0, 0)" />

                  {/* Graduation cap - 3D effect */}
                  {/* Cap shadow */}
                  <ellipse cx="0" cy="0" rx="42" ry="8" fill="rgba(255,255,255,0.05)" />

                  {/* Cap base/brim */}
                  <path d="M-48 2 L48 2" stroke="rgba(255,255,255,0.9)" strokeWidth="5" strokeLinecap="round" />

                  {/* Cap top */}
                  <path d="M-44 0 L0 -38 L44 0" stroke="rgba(255,255,255,0.95)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" fill="rgba(255,255,255,0.1)" />

                  {/* Cap top fill for 3D effect */}
                  <path d="M-44 0 L0 -38 L44 0" stroke="rgba(255,255,255,0.95)" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round" />

                  {/* Highlight on cap */}
                  <path d="M-30 -12 L0 -32 L30 -12" stroke="rgba(255,255,255,0.3)" strokeWidth="2" fill="none" />

                  {/* Tassel */}
                  <path d="M0 -38 C5 -35, 20 -20, 25 -15 C28 -12, 30 -8, 28 -5" stroke="rgba(255,215,0,0.8)" strokeWidth="2.5" fill="none" />
                  <circle cx="28" cy="-4" r="4" fill="rgba(255,215,0,0.8)" />
                  <circle cx="28" cy="-4" r="2" fill="rgba(255,215,0,0.5)" />

                  {/* Star on cap */}
                  <path d="M0 -30 L2 -24 L9 -24 L4 -20 L6 -14 L0 -17 L-6 -14 L-4 -20 L-9 -24 L-2 -24 Z" fill="rgba(255,215,0,0.9)" stroke="rgba(255,215,0,0.3)" strokeWidth="0.5" />
                </g>

                {/* Floating particles */}
                <g opacity="0.6">
                  <circle cx="35" cy="45" r="3" fill="rgba(255,255,255,0.4)" />
                  <circle cx="160" cy="50" r="2.5" fill="rgba(255,255,255,0.35)" />
                  <circle cx="45" cy="155" r="2" fill="rgba(255,255,255,0.3)" />
                  <circle cx="155" cy="145" r="3.5" fill="rgba(255,255,255,0.3)" />
                  <circle cx="25" cy="95" r="2" fill="rgba(255,255,255,0.25)" />
                  <circle cx="175" cy="105" r="2.5" fill="rgba(255,255,255,0.25)" />
                  <circle cx="60" cy="30" r="1.5" fill="rgba(255,255,255,0.2)" />
                  <circle cx="140" cy="35" r="2" fill="rgba(255,255,255,0.2)" />
                  <circle cx="30" cy="135" r="1.5" fill="rgba(255,255,255,0.2)" />
                  <circle cx="170" cy="130" r="2" fill="rgba(255,255,255,0.2)" />
                </g>

                {/* Sparkle effects */}
                <g opacity="0.5">
                  <path d="M75 30 L77 26 L79 30 L83 32 L79 34 L77 38 L75 34 L71 32 Z" fill="rgba(255,215,0,0.4)" />
                  <path d="M125 160 L127 156 L129 160 L133 162 L129 164 L127 168 L125 164 L121 162 Z" fill="rgba(255,215,0,0.3)" />
                  <path d="M170 75 L172 71 L174 75 L178 77 L174 79 L172 83 L170 79 L166 77 Z" fill="rgba(255,215,0,0.3)" />
                </g>
              </svg>

              <div className="mt-4 text-center">
                <p className="text-blue-100 text-base font-medium tracking-wide opacity-90">
                  Share Your Experience
                </p>
                <p className="text-blue-200 text-xs opacity-60 mt-1">
                  Help us make learning better
                </p>
              </div>
            </div>
          </div>

          <div className="md:w-3/5 p-8">
            <motion.h1
              initial={{ opacity: 0, y: -20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-2xl md:text-3xl font-bold text-gray-800 mb-2"
            >
              Course Feedback
            </motion.h1>
            <p className="text-gray-600 mb-3">Help us improve your learning experience</p>

            <form onSubmit={handleSubmit}>
              <div className="mb-3">
                <label htmlFor="name" className="block text-gray-700 font-medium mb-2">Your Name (Optional)</label>
                <input
                  type="text"
                  id="name"
                  value={name}
                  onChange={(e) => setName(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg transition-all"
                  placeholder="Alex Johnson"
                />
              </div>

              <div className="mb-3">
                <label htmlFor="course" className="block text-gray-700 font-medium mb-2">Select Course</label>
                <select
                  id="course"
                  value={course}
                  onChange={(e) => setCourse(e.target.value)}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg transition-all"
                  required
                >
                  <option value="">-- Select a course --</option>
                  {courses.map((courseOption, index) => (
                    <option key={index} value={courseOption}>{courseOption}</option>
                  ))}
                </select>
              </div>

              <div className="mb-3">
                <label className="block text-gray-700 font-medium mb-4">How would you rate this course?</label>
                <div className="flex justify-between items-center mb-2">
                  {[...Array(5)].map((_, index) => {
                    const ratingValue = index + 1;
                    return (
                      <motion.button
                        type="button"
                        key={index}
                        whileHover={{ scale: 1.1 }}
                        whileTap={{ scale: 0.9 }}
                        className="flex flex-col items-center"
                        onClick={() => setRating(ratingValue)}
                        onMouseEnter={() => setHover(ratingValue)}
                        onMouseLeave={() => setHover(rating)}
                      >
                        <StarIcon filled={ratingValue <= (hover || rating)} />
                        <span className="text-xs mt-1 text-gray-500">{ratings[index]}</span>
                        <div className="w-8 h-8 mt-1">
                          {emojis[index]}
                        </div>
                      </motion.button>
                    );
                  })}
                </div>
              </div>

              <div className="mb-3">
                <label htmlFor="feedback" className="block text-gray-700 font-medium mb-2">Your Feedback</label>
                <textarea
                  id="feedback"
                  value={feedback}
                  onChange={(e) => setFeedback(e.target.value)}
                  rows="4"
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg transition-all"
                  placeholder="What did you like about the course? What could be improved?"
                  required
                ></textarea>
                <p className="text-xs text-gray-500 mt-1">Be specific - your comments help instructors improve!</p>
              </div>

              <motion.button
                type="submit"
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.98 }}
                className="w-full bg-gradient-to-r from-[#0d4f5c] to-[#1a7a8a] text-white font-medium py-3 px-4 rounded-lg shadow-md hover:shadow-lg transition-all duration-300 flex items-center justify-center"
              >
                <svg className="w-5 h-5 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 5l7 7-7 7M5 5l7 7-7 7" />
                </svg>
                Submit Feedback
              </motion.button>
            </form>
          </div>
        </div>
      </div>
    </motion.div>
  );
};

export default StudentFeedback;